<template>
  <div>
    <span
      v-if="interval && showTimer"
      class="refresh-cd"
      v-html="$t(labelPath, { count: `<strong>${remainingSeconds}</strong>` })"
    />
    <span
      v-if="interval && !showTimer"
      class="refresh-cd">
        {{ getLastUpdateText }}
      </span>
  </div>
</template>

<script>
/**
 * Label para refresh automático.
 *
 * O intervalo de refresh segue a definição de parametros do sistema (autoRefreshInterval).
 *
 * Para usar, basta importa-lo e monitorar o evento on-interval:
 *
 * <auto-refresh-countdown-label @on-interval="..."/>
 *
 * Ver documentações adicinais abaixo e na sessão de declaração de props.
 *
 */
/**
 * @event on-interval Evento disparado no final do intervalo.
 * Opcionalmente, este evento disponibiliza um utilitário (wait) que pode ser chamado para aguardar
 * uma carga de dados.
 * @example
 * <auto-refresh-countdown-label @on-interval="$event.wait(doRequest)" />
 * @property {Function} wait Função function(Promise) para informar uma promessa para ser aguardada.
 * Enquanto a promessa não acabar, o timer fica suspenso, indicando o tempo máximo.
 */
import { mapGetters } from 'vuex';
import { Interval } from '@/services/timer';
import {
  formatter as dateTimeFormatter,
  getNow,
} from '@/services/dateTime/datetimeService';
import { formats } from '@/constants/localizer';

export default {
  components: {
  },
  props: {
    /**
     * Caminho do path de tradução do label. Opcional, se definido, insere o tempo em {count}.
     */
    labelPath: {
      type: String,
      default: 'refresh-countdown-label.text',
      required: false,
    },
    /**
     * Desabilita o contador. Quando desabilitado, o tempo para. Quando reabilitado, o contador
     * recomeça.
     */
    disabled: Boolean,
    showTimer: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      intervalMonitor: undefined,
      remainingSeconds: undefined,
      intervalWaiting: false,
      lastUpdate: '',
    };
  },
  methods: {
    initCountdown() {
      if (!this.interval) return;
      this.resetCounting();
      this.createIntervalMonitor();
      this.tryStart();
    },
    deinitCountdown() {
      if (!this.interval) return;
      this.tryStop();
      this.intervalMonitor = null;
    },
    createIntervalMonitor() {
      this.intervalMonitor = new Interval(this.interval * 1000);
      this.intervalMonitor.onTick.push(remainingMs => this.onTick(remainingMs));
      this.intervalMonitor.onInterval.push(() => this.onInterval());
    },
    tryStart() {
      if (!this.suspended) {
        this.intervalMonitor.start();
      }
    },
    tryStop() {
      if (!this.suspended) {
        this.intervalMonitor.stop();
      }
    },
    resetCounting() {
      this.remainingSeconds = this.interval;
      this.lastUpdate = getNow();
    },
    onTick(remainingMs) {
      this.remainingSeconds = Math.trunc(remainingMs / 1000);
    },
    onInterval() {
      this.$emit('on-interval', {
        wait: promise => this.waitOnInterval(promise),
      });
    },
    async waitOnInterval(promise) {
      this.resetCounting();
      this.intervalWaiting = true;
      try {
        await promise;
      } finally {
        this.intervalWaiting = false;
      }
    },
  },
  watch: {
    suspended() {
      if (!this.suspended) {
        this.intervalMonitor.start();
      } else {
        this.intervalMonitor.stop();
      }
    },
  },
  computed: {
    ...mapGetters({
      interval: 'autoRefreshInterval',
    }),
    suspended() {
      return this.disabled || this.intervalWaiting;
    },
    getLastUpdateText() {
      const dateTime = dateTimeFormatter(this.lastUpdate, formats.DATETIME_SHORT_WITH_SECONDS);
      const [date, time] = dateTime.split(', ');

      return `${this.$t('dashboard.last-updated-on')} ${date} ${this.$t('general.at')} ${time}`;
    },
  },
  destroyed() {
    this.deinitCountdown();
  },
  created() {
    this.initCountdown();
  },
};
</script>
